import { ChevronDown, Download, FileText, Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '@/components/BatchList';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatFileSize, toDate, type AnalysisRunFileItem } from '@/lib/analyses/runList';
import { translateKey } from '@/lib/analyses/translateKey';

type OnDownload = (file: AnalysisRunFileItem) => void;

const itemClass = 'flex cursor-pointer items-center gap-3 py-2';

function FileWhen({ file, nowMs }: { file: AnalysisRunFileItem; nowMs: number }) {
  // Relative-time copy lives in the components namespace (see AnalysisRunRow).
  const { t: tComponents } = useTranslation('components');
  return <>{formatDateTime(toDate(file.createdAt), (k, o) => translateKey(tComponents, k, o), nowMs)}</>;
}

/**
 * "PDF": one report → a plain download button; several (newest first) → a
 * dropdown of filename + date. Renders nothing without a PDF.
 */
export function RunPdfAction({
  runId,
  pdfs,
  nowMs,
  onDownload,
}: {
  runId: string;
  pdfs: AnalysisRunFileItem[];
  /** The list's clock, so "N minutes ago" advances with it. */
  nowMs: number;
  onDownload: OnDownload;
}) {
  const { t } = useTranslation('project');
  if (pdfs.length === 0) return null;
  const label = (
    <>
      <FileText className='size-4' />
      <span className='ml-1 hidden sm:inline'>{t('analyses.files.pdf')}</span>
    </>
  );
  if (pdfs.length === 1) {
    const pdf = pdfs[0];
    return (
      <Button
        variant='ghost'
        size='sm'
        data-testid={`analysis-run-pdf-${runId}`}
        title={pdf.filename}
        aria-label={`${t('analyses.files.pdf')}: ${pdf.filename}`}
        onClick={() => onDownload(pdf)}>
        {label}
      </Button>
    );
  }
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          data-testid={`analysis-run-pdf-${runId}`}
          aria-label={`${t('analyses.files.pdfReports')} (${pdfs.length})`}>
          {label}
          <span className='ml-1 text-xs tabular-nums text-slate-500 dark:text-stone-400'>{pdfs.length}</span>
          <ChevronDown className='size-3.5 text-slate-400 dark:text-stone-500' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-80'>
        <DropdownMenuLabel className='text-xs font-medium text-slate-500 dark:text-stone-400'>
          {t('analyses.files.pdfReports')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {pdfs.map((pdf) => (
          <DropdownMenuItem
            key={pdf.id}
            data-testid={`analysis-run-pdf-item-${runId}-${pdf.id}`}
            className={itemClass}
            onSelect={() => onDownload(pdf)}>
            <FileText className='size-4 shrink-0 text-rose-500 dark:text-rose-400' />
            <div className='min-w-0 flex-1'>
              <div className='truncate text-sm text-slate-900 dark:text-stone-100' title={pdf.filename}>
                {pdf.filename}
              </div>
              <div className='text-xs text-slate-500 dark:text-stone-400'>
                <FileWhen file={pdf} nowMs={nowMs} />
                {formatFileSize(pdf.contentLength) && ` · ${formatFileSize(pdf.contentLength)}`}
              </div>
            </div>
            <Download className='size-3.5 shrink-0 text-slate-400 dark:text-stone-500' />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** "Files (n)": every non-PDF output, with a type badge and its size. */
export function RunFilesMenu({ runId, files, onDownload }: { runId: string; files: AnalysisRunFileItem[]; onDownload: OnDownload }) {
  const { t } = useTranslation('project');
  if (files.length === 0) return null;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          data-testid={`analysis-run-files-${runId}`}
          aria-label={t('analyses.files.filesCount', { count: files.length })}>
          <Paperclip className='size-4' />
          <span className='ml-1 hidden sm:inline'>{t('analyses.files.files')}</span>
          <span className='ml-1 text-xs tabular-nums text-slate-500 dark:text-stone-400'>{files.length}</span>
          <ChevronDown className='size-3.5 text-slate-400 dark:text-stone-500' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-80'>
        <DropdownMenuLabel className='text-xs font-medium text-slate-500 dark:text-stone-400'>
          {t('analyses.files.files')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {files.map((file) => {
          const size = formatFileSize(file.contentLength);
          return (
            <DropdownMenuItem
              key={file.id}
              data-testid={`analysis-run-file-${runId}-${file.id}`}
              className={itemClass}
              onSelect={() => onDownload(file)}>
              <span
                data-testid='analysis-run-file-type'
                className='w-16 shrink-0 truncate rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-300'>
                {file.type}
              </span>
              <span className='min-w-0 flex-1 truncate text-sm text-slate-900 dark:text-stone-100' title={file.filename}>
                {file.filename}
              </span>
              {size && (
                <span className='shrink-0 text-xs tabular-nums text-slate-500 dark:text-stone-400'>{size}</span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
